import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, TextField, Button, Switch,
  FormControlLabel, Alert, Chip, Divider, CircularProgress, InputAdornment, IconButton, Tooltip,
} from '@mui/material';
import { Instagram, Facebook, CheckCircle, ContentCopy, Save } from '@mui/icons-material';
import { api } from '../config/api';

const RedesSociales: React.FC = () => {
  const [cfg, setCfg] = useState<any>({ instagram_activo: false, messenger_activo: false });
  const [webhookUrl, setWebhookUrl] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ t: string; sev: any } | null>(null);
  const [verificando, setVerificando] = useState(false);

  const cargar = () => {
    setLoading(true);
    api.get('/social').then((r) => {
      setWebhookUrl(r.data.webhookUrl);
      if (r.data.config) { setCfg({ ...cfg, ...r.data.config }); setVerifyToken(r.data.config.verify_token || ''); }
    }).finally(() => setLoading(false));
  };
  useEffect(cargar, []); // eslint-disable-line

  const guardar = async () => {
    setMsg(null);
    const { data } = await api.put('/social', cfg);
    if (data.verify_token) setVerifyToken(data.verify_token);
    setMsg({ t: 'Configuración guardada', sev: 'success' });
    cargar();
  };

  const verificar = async () => {
    setVerificando(true); setMsg(null);
    try {
      const { data } = await api.post('/social/verificar');
      if (data.ok) setMsg({ t: `Conectado a la página: ${data.nombre}`, sev: 'success' });
      else setMsg({ t: `Error: ${data.error}`, sev: 'error' });
    } finally { setVerificando(false); }
  };

  const copiar = (t: string) => { navigator.clipboard.writeText(t); setMsg({ t: 'Copiado', sev: 'info' }); };

  if (loading) return <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Instagram sx={{ color: '#E1306C' }} />
        <Facebook sx={{ color: '#1877F2' }} />
        <Typography variant="h4">Instagram y Messenger</Typography>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Conecta tu Instagram Business y tu página de Facebook para que el agente responda los mensajes directos.
        Usa tu propia app de Meta (modelo de credenciales propias).
      </Typography>

      {msg && <Alert severity={msg.sev} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.t}</Alert>}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Credenciales de tu app de Meta</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            En developers.facebook.com crea una app, vincula tu página de Facebook y tu cuenta de Instagram Business,
            y genera un <strong>Page Access Token</strong>. Luego pega aquí los datos.
          </Alert>
          <Stack spacing={2}>
            <TextField label="Page Access Token" fullWidth type="password"
              placeholder={cfg.tiene_token ? '•••••••• (guardado, escribe para cambiar)' : 'EAAG...'}
              onChange={(e) => setCfg({ ...cfg, page_access_token: e.target.value })} />
            <TextField label="Page ID (página de Facebook)" fullWidth value={cfg.page_id || ''} onChange={(e) => setCfg({ ...cfg, page_id: e.target.value })} />
            <TextField label="Instagram Business ID" fullWidth value={cfg.ig_business_id || ''} onChange={(e) => setCfg({ ...cfg, ig_business_id: e.target.value })} />
            <TextField label="App Secret (opcional)" fullWidth type="password" onChange={(e) => setCfg({ ...cfg, app_secret: e.target.value })} />
            <Stack direction="row" spacing={3}>
              <FormControlLabel control={<Switch checked={!!cfg.messenger_activo} onChange={(e) => setCfg({ ...cfg, messenger_activo: e.target.checked })} />} label={<Stack direction="row" spacing={0.5} alignItems="center"><Facebook sx={{ color: '#1877F2' }} fontSize="small" /> Messenger</Stack>} />
              <FormControlLabel control={<Switch checked={!!cfg.instagram_activo} onChange={(e) => setCfg({ ...cfg, instagram_activo: e.target.checked })} />} label={<Stack direction="row" spacing={0.5} alignItems="center"><Instagram sx={{ color: '#E1306C' }} fontSize="small" /> Instagram</Stack>} />
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" startIcon={<Save />} onClick={guardar}>Guardar</Button>
              <Button variant="outlined" onClick={verificar} disabled={verificando}>{verificando ? 'Verificando...' : 'Verificar conexión'}</Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 1 }}>Webhook de Meta</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            En tu app de Meta → Webhooks, configura esta URL y el token. Suscríbete a los campos <strong>messages</strong> de Messenger e Instagram.
          </Typography>
          <TextField label="Callback URL" fullWidth sx={{ mb: 2 }} value={webhookUrl} InputProps={{
            readOnly: true,
            endAdornment: <InputAdornment position="end"><Tooltip title="Copiar"><IconButton onClick={() => copiar(webhookUrl)}><ContentCopy fontSize="small" /></IconButton></Tooltip></InputAdornment>,
          }} />
          <TextField label="Verify Token" fullWidth value={verifyToken} InputProps={{
            readOnly: true,
            endAdornment: <InputAdornment position="end"><Tooltip title="Copiar"><IconButton onClick={() => copiar(verifyToken)}><ContentCopy fontSize="small" /></IconButton></Tooltip></InputAdornment>,
          }} helperText="Se genera al guardar por primera vez" />
          <Divider sx={{ my: 2 }} />
          {(cfg.instagram_activo || cfg.messenger_activo) && (
            <Chip icon={<CheckCircle />} color="success" label={`Activo: ${[cfg.messenger_activo && 'Messenger', cfg.instagram_activo && 'Instagram'].filter(Boolean).join(' + ')}`} />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default RedesSociales;
